import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useUser } from "../../../Home/UserContext";
import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  addAdherentExerciseResult,
  createPrepaExercise,
  deleteAdherentExerciseResult,
  deletePrepaExercise,
  editPrepaExercise,
  getAllAdherentPrepaExercises,
  getAllPrepaExercises,
} from "../../../../services/prepaPhysiqueServices";

import {
  getGroupsByUser,
  getGroupAdherents,
} from "../../../../services/groupServices";

import {
  DEFAULT_ASSIGNMENT_FORM,
  DEFAULT_EXERCISE_FORM,
  buildPrepaStats,
  buildResultKey,
  dedupeAdherents,
  getTodayInputDate,
  normalizeExerciseFormFromApi,
  normalizeExercisePayload,
  sortAssignedResults,
  sortExercises,
  validateAssignmentForm,
  validateExerciseForm,
} from "../utils/preparationPhysiqueUtils";

function getUserId(user) {
  return user?.userId || user?.id || null;
}

export function usePreparationPhysiqueCoachPage() {
  const { user } = useUser();
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);

  const [exercises, setExercises] = useState([]);
  const [groups, setGroups] = useState([]);
  const [adherentsByGroup, setAdherentsByGroup] = useState({});
  const [assignedResults, setAssignedResults] = useState([]);

  const [editingExercise, setEditingExercise] = useState(null);
  const [selectedExerciseForAssign, setSelectedExerciseForAssign] =
    useState(null);

  const [exerciseForm, setExerciseForm] = useState(DEFAULT_EXERCISE_FORM);

  const [assignmentForm, setAssignmentForm] = useState({
    ...DEFAULT_ASSIGNMENT_FORM,
    date_realisation: getTodayInputDate(),
  });

  const [selectedAdherentIds, setSelectedAdherentIds] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingExercise, setSavingExercise] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [deletingExerciseIds, setDeletingExerciseIds] = useState([]);
  const [deletingResultKeys, setDeletingResultKeys] = useState([]);

  const userId = useMemo(() => getUserId(user), [user]);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  const allAdherents = useMemo(() => {
    return dedupeAdherents(Object.values(adherentsByGroup).flat());
  }, [adherentsByGroup]);

  const coachAdherentIds = useMemo(() => {
    return new Set(allAdherents.map((adherent) => Number(adherent.id)));
  }, [allAdherents]);

  const visibleAssignedResults = useMemo(() => {
    if (!coachAdherentIds.size) {
      return sortAssignedResults(assignedResults);
    }

    return sortAssignedResults(
      assignedResults.filter((result) => {
        const adherentId = Number(result?.adherent_id || result?.Adherent?.id);
        return coachAdherentIds.has(adherentId);
      }),
    );
  }, [assignedResults, coachAdherentIds]);

  const stats = useMemo(() => {
    return buildPrepaStats({
      exercises,
      groups,
      allAdherents,
      assignedResults: visibleAssignedResults,
    });
  }, [allAdherents, exercises, groups, visibleAssignedResults]);

  const fetchGroupsAndAdherents = useCallback(async () => {
    if (!userId) {
      setGroups([]);
      setAdherentsByGroup({});
      return [];
    }

    const groupsResponse = await getGroupsByUser(userId);
    const safeGroups = Array.isArray(groupsResponse) ? groupsResponse : [];

    const entries = await Promise.all(
      safeGroups.map(async (group) => {
        try {
          const response = await getGroupAdherents(group.id);
          const adherents = Array.isArray(response?.adherents)
            ? response.adherents
            : [];

          return [group.id, adherents];
        } catch (error) {
          console.error(
            `Erreur récupération adhérents groupe ${group.id} :`,
            error,
          );
          return [group.id, []];
        }
      }),
    );

    setGroups(safeGroups);
    setAdherentsByGroup(Object.fromEntries(entries));

    return safeGroups;
  }, [userId]);

  const fetchData = useCallback(
    async ({ silent = false } = {}) => {
      const isInitialLoad = !hasLoadedOnceRef.current;

      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const [exercisesResponse, assignedResponse] = await Promise.all([
          getAllPrepaExercises(),
          getAllAdherentPrepaExercises(),
          fetchGroupsAndAdherents(),
        ]);

        const safeExercises = Array.isArray(exercisesResponse)
          ? exercisesResponse
          : [];

        const safeAssigned = Array.isArray(assignedResponse)
          ? assignedResponse
          : [];

        setExercises(sortExercises(safeExercises));
        setAssignedResults(sortAssignedResults(safeAssigned));

        if (!silent && !isInitialLoad) {
          notificationRef.current?.(
            "Préparation physique actualisée.",
            "success",
          );
        }
      } catch (error) {
        console.error("Erreur récupération préparation physique :", error);

        notificationRef.current?.(
          "Erreur lors de la récupération de la préparation physique.",
          "error",
        );
      } finally {
        hasLoadedOnceRef.current = true;
        setLoading(false);
        setRefreshing(false);
      }
    },
    [fetchGroupsAndAdherents],
  );

  useEffect(() => {
    fetchData({ silent: true });
  }, [fetchData]);

  const handleExerciseInputChange = useCallback((event) => {
    const { name, value } = event.target;

    setExerciseForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleAssignmentInputChange = useCallback((event) => {
    const { name, value, type, checked } = event.target;

    setAssignmentForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  const handleSubmitExercise = useCallback(
    async (event) => {
      event.preventDefault();

      const error = validateExerciseForm(exerciseForm);

      if (error) {
        notificationRef.current?.(error, "error");
        return;
      }

      setSavingExercise(true);

      try {
        const payload = normalizeExercisePayload(exerciseForm);

        if (editingExercise?.id) {
          await editPrepaExercise(editingExercise.id, payload);

          notificationRef.current?.(
            "Exercice modifié avec succès.",
            "success",
          );
        } else {
          await createPrepaExercise(payload);

          notificationRef.current?.("Exercice créé avec succès.", "success");
        }

        setEditingExercise(null);
        setExerciseForm(DEFAULT_EXERCISE_FORM);

        await fetchData({ silent: true });
      } catch (error) {
        console.error("Erreur enregistrement exercice :", error);

        notificationRef.current?.(
          error?.response?.data?.message ||
            "Erreur lors de l'enregistrement de l'exercice.",
          "error",
        );
      } finally {
        setSavingExercise(false);
      }
    },
    [editingExercise, exerciseForm, fetchData],
  );

  const handleEditExercise = useCallback((exercise) => {
    setEditingExercise(exercise);
    setExerciseForm(normalizeExerciseFormFromApi(exercise));
  }, []);

  const handleCancelExerciseEdit = useCallback(() => {
    setEditingExercise(null);
    setExerciseForm(DEFAULT_EXERCISE_FORM);
  }, []);

  const handleDeleteExercise = useCallback(
    async (exercise) => {
      if (!exercise?.id) return;

      setDeletingExerciseIds((prev) => [...prev, exercise.id]);

      try {
        await deletePrepaExercise(exercise.id);

        notificationRef.current?.("Exercice supprimé avec succès.", "success");

        if (editingExercise?.id === exercise.id) {
          setEditingExercise(null);
          setExerciseForm(DEFAULT_EXERCISE_FORM);
        }

        if (selectedExerciseForAssign?.id === exercise.id) {
          setSelectedExerciseForAssign(null);
        }

        await fetchData({ silent: true });
      } catch (error) {
        console.error("Erreur suppression exercice :", error);

        notificationRef.current?.(
          error?.response?.data?.message ||
            "Erreur lors de la suppression de l'exercice.",
          "error",
        );
      } finally {
        setDeletingExerciseIds((prev) =>
          prev.filter((id) => id !== exercise.id),
        );
      }
    },
    [editingExercise, fetchData, selectedExerciseForAssign],
  );

  const handleDeleteExerciseWithConfirm = useCallback(
    async (exercise) => {
      const confirmDelete = window.confirm(
        `Supprimer l'exercice "${exercise?.nom || ""}" ? Les résultats associés peuvent aussi être supprimés selon ta contrainte SQL.`,
      );

      if (!confirmDelete) return;

      await handleDeleteExercise(exercise);
    },
    [handleDeleteExercise],
  );

  const handleSelectExerciseForAssign = useCallback((exercise) => {
    setSelectedExerciseForAssign(exercise);
  }, []);

  const handleToggleAdherent = useCallback((adherentId) => {
    const normalizedId = Number(adherentId);

    setSelectedAdherentIds((prev) => {
      if (prev.includes(normalizedId)) {
        return prev.filter((id) => id !== normalizedId);
      }

      return [...prev, normalizedId];
    });
  }, []);

  const handleToggleGroup = useCallback(
    (groupId) => {
      const groupAdherents = adherentsByGroup[groupId] || [];

      const groupIds = groupAdherents
        .map((adherent) => Number(adherent.id))
        .filter(Boolean);

      if (!groupIds.length) return;

      setSelectedAdherentIds((prev) => {
        const allSelected = groupIds.every((id) => prev.includes(id));

        if (allSelected) {
          return prev.filter((id) => !groupIds.includes(id));
        }

        return Array.from(new Set([...prev, ...groupIds]));
      });
    },
    [adherentsByGroup],
  );

  const handleClearSelection = useCallback(() => {
    setSelectedAdherentIds([]);
  }, []);

  const handleAssignExercise = useCallback(
    async (event) => {
      event.preventDefault();

      const error = validateAssignmentForm({
        selectedExercise: selectedExerciseForAssign,
        selectedAdherentIds,
        assignmentForm,
      });

      if (error) {
        notificationRef.current?.(error, "error");
        return;
      }

      setAssigning(true);

      try {
        const requests = selectedAdherentIds.map((adherentId) =>
          addAdherentExerciseResult({
            adherent_id: adherentId,
            prepa_physique_id: selectedExerciseForAssign.id,
            date_realisation: assignmentForm.date_realisation,
            repetitions: assignmentForm.repetitions,
            is_done: false,
          }),
        );

        const results = await Promise.allSettled(requests);

        const successCount = results.filter(
          (result) => result.status === "fulfilled",
        ).length;

        const errorCount = results.length - successCount;

        if (successCount > 0) {
          notificationRef.current?.(
            `${successCount} attribution${
              successCount > 1 ? "s" : ""
            } créée${successCount > 1 ? "s" : ""}.`,
            "success",
          );
        }

        if (errorCount > 0) {
          notificationRef.current?.(
            `${errorCount} attribution${
              errorCount > 1 ? "s" : ""
            } non créée${errorCount > 1 ? "s" : ""}. Vérifie les doublons éventuels.`,
            "error",
          );
        }

        setSelectedAdherentIds([]);

        setAssignmentForm({
          ...DEFAULT_ASSIGNMENT_FORM,
          date_realisation: getTodayInputDate(),
        });

        await fetchData({ silent: true });
      } catch (error) {
        console.error("Erreur attribution exercice :", error);

        notificationRef.current?.(
          "Erreur lors de l'attribution de l'exercice.",
          "error",
        );
      } finally {
        setAssigning(false);
      }
    },
    [
      assignmentForm,
      fetchData,
      selectedAdherentIds,
      selectedExerciseForAssign,
    ],
  );

  const handleDeleteResult = useCallback(
    async (result) => {
      const adherentId = Number(result?.adherent_id || result?.Adherent?.id);

      const exerciseId = Number(
        result?.prepa_physique_id || result?.Exercice?.id,
      );

      const resultKey = buildResultKey(result);

      if (!adherentId || !exerciseId) return;

      setDeletingResultKeys((prev) => [...prev, resultKey]);

      try {
        await deleteAdherentExerciseResult(adherentId, exerciseId);

        notificationRef.current?.("Attribution supprimée.", "success");

        await fetchData({ silent: true });
      } catch (error) {
        console.error("Erreur suppression attribution :", error);

        notificationRef.current?.(
          "Erreur lors de la suppression de l'attribution.",
          "error",
        );
      } finally {
        setDeletingResultKeys((prev) =>
          prev.filter((key) => key !== resultKey),
        );
      }
    },
    [fetchData],
  );

  const handleDeleteResultWithConfirm = useCallback(
    async (result) => {
      const confirmDelete = window.confirm(
        "Supprimer cette attribution de préparation physique ?",
      );

      if (!confirmDelete) return;

      await handleDeleteResult(result);
    },
    [handleDeleteResult],
  );

  return {
    user,
    exercises,
    groups,
    adherentsByGroup,
    allAdherents,
    assignedResults: visibleAssignedResults,
    editingExercise,
    selectedExerciseForAssign,
    exerciseForm,
    assignmentForm,
    selectedAdherentIds,
    loading,
    refreshing,
    savingExercise,
    assigning,
    deletingExerciseIds,
    deletingResultKeys,
    stats,
    fetchData,
    handleExerciseInputChange,
    handleSubmitExercise,
    handleEditExercise,
    handleCancelExerciseEdit,
    handleDeleteExerciseWithConfirm,
    handleSelectExerciseForAssign,
    handleAssignmentInputChange,
    handleToggleAdherent,
    handleToggleGroup,
    handleClearSelection,
    handleAssignExercise,
    handleDeleteResultWithConfirm,
  };
}